// Print catalogue (/printing/all) data: the client's 7 categories, the
// popularity order, and the quick-view "toggle dropdown" sections.
//
// Client notes 2026-10-05 (client-updates/outpost-updates.md):
// - CATEGORY_OF and POPULARITY are provisional. HD is tagging the products and
//   sending the popularity order; replace both lists when they arrive.
// - PRODUCT_DETAILS is empty until HD supplies per-product minimum order,
//   sizes, paper stock, extras and delivery. Until then the Sizes / Paper Stock
//   / Extras sections fall back to what the product description mentions.
import type { PrintingProduct } from '../../lib/printing-products';

export const PRINT_CATEGORIES = [
  { id: 'stickers-labels', label: 'Stickers & Labels' },
  { id: 'business-stationery', label: 'Business Stationery' },
  { id: 'events', label: 'Events' },
  { id: 'cards-invites', label: 'Cards & Invites' },
  { id: 'art', label: 'Art' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'packaging', label: 'Packaging' },
] as const;

export type PrintCategoryId = (typeof PRINT_CATEGORIES)[number]['id'];

// Product slug -> categories. A product may sit in more than one.
const CATEGORY_OF: Record<string, PrintCategoryId[]> = {
  'adhesive-product-labels': ['stickers-labels', 'packaging'],
  'rectangle-and-square-sticker-sheets': ['stickers-labels'],
  'round-sticker-sheets': ['stickers-labels'],

  'printed-business-cards': ['business-stationery'],
  'printed-folded-business-cards': ['business-stationery'],
  'printed-letterheads': ['business-stationery'],
  'printed-compliment-slips': ['business-stationery'],
  'printed-appointment-cards': ['business-stationery'],
  'printed-folded-appointment-cards': ['business-stationery'],
  'printed-loyalty-cards': ['business-stationery'],
  'printed-folded-loyalty-cards': ['business-stationery'],
  'notepads': ['business-stationery'],
  'printed-certificates': ['business-stationery'],
  'wire-bound-documents': ['business-stationery'],
  'wire-bound-notebooks': ['business-stationery'],

  'printed-flyers-and-leaflets': ['events'],
  'printed-folded-flyers-and-leaflets': ['events'],
  'printed-posters': ['events', 'art'],
  'booklets-and-brochures': ['events', 'business-stationery'],
  'table-talkers': ['events'],
  'table-tents': ['events'],
  'scratch-cards': ['events'],
  'funeral-order-of-service-booklets': ['events'],

  // Placeholder content and images until HD sends them (2026-10-07)
  'menus': ['business-stationery', 'events'],
  'price-lists': ['business-stationery'],
  'ncr-pads': ['business-stationery'],
  'wall-planners': ['calendars', 'business-stationery'],
  'invitations': ['events', 'cards-invites'],

  'printed-greetings-cards': ['cards-invites'],
  'printed-christmas-cards': ['cards-invites'],
  'printed-postcards': ['cards-invites'],
  'printed-gift-vouchers': ['cards-invites'],

  'art-prints': ['art'],
  'printed-bookmarks': ['art'],

  'printed-desk-calendars': ['calendars'],
  'printed-wall-calendars': ['calendars'],

  'clothing-swing-tags': ['packaging'],
  'jewellery-backing-cards': ['packaging'],
  'packaging-sleeves-and-belly-bands': ['packaging'],
  'pin-badge-backing-cards': ['packaging'],
};

// Most popular first. Slugs not listed keep their JSON order after these.
const POPULARITY: string[] = [
  'printed-business-cards',
  'printed-flyers-and-leaflets',
  'booklets-and-brochures',
  'printed-posters',
  'adhesive-product-labels',
  'printed-folded-flyers-and-leaflets',
  'printed-postcards',
  'printed-greetings-cards',
  'printed-loyalty-cards',
  'printed-letterheads',
  'printed-compliment-slips',
  'round-sticker-sheets',
  'rectangle-and-square-sticker-sheets',
  'clothing-swing-tags',
  'art-prints',
  'printed-wall-calendars',
  'printed-desk-calendars',
  'printed-christmas-cards',
  'printed-gift-vouchers',
  'printed-appointment-cards',
];

export function getPrintCategories(product: PrintingProduct): PrintCategoryId[] {
  return CATEGORY_OF[product.slug] ?? [];
}

export function sortByPopularity(products: PrintingProduct[]): PrintingProduct[] {
  const rank = (slug: string) => {
    const i = POPULARITY.indexOf(slug);
    return i === -1 ? POPULARITY.length : i;
  };
  // Array.prototype.sort is stable, so unranked products keep their order.
  return [...products].sort((a, b) => rank(a.slug) - rank(b.slug));
}

// ---------------------------------------------------------------------------
// Quick-view sections ("toggle dropdown question section")

export interface PrintDetailSection {
  title: string;
  items: { text: string; href?: string }[];
}

// Per-product overrides from HD, keyed by slug. Any section given here
// replaces the default for that product.
interface ProductDetailOverride {
  specification?: string[]; // extra lines, e.g. binding options for booklets
  minimumOrder?: string; // e.g. "50"
  sizes?: string[];
  paperStock?: string[];
  extras?: string[];
}

const PRODUCT_DETAILS: Record<string, ProductDetailOverride> = {};

const EMAIL = 'info@outpostcustom.co.uk';

const ARTWORK_ITEMS: PrintDetailSection['items'] = [
  { text: 'FREE artwork check on all supplied artwork' },
  { text: `Submit PDF artwork via email to ${EMAIL}`, href: `mailto:${EMAIL}?subject=Print artwork` },
  { text: 'Submit your text and logo to our design team to create using our standard template (artwork fee from £30+VAT)' },
  { text: `Set up your artwork in Canva and share it to ${EMAIL} from £15+VAT` },
  { text: 'Download FREE InDesign templates' },
  { text: 'Speak to our Design Team for a bespoke design quote' },
];

// Fallback specs read from the product description (the old 3-column view).
function parseFromDescription(product: PrintingProduct) {
  const desc = product.full_description;
  const lower = desc.toLowerCase();

  const sizes: string[] = [];
  if (desc.includes('85mm x 55mm') || desc.includes('85 x 55')) sizes.push('85 x 55mm');
  if (desc.includes('A3')) sizes.push('A3');
  if (desc.includes('A4')) sizes.push('A4');
  if (desc.includes('A5')) sizes.push('A5');
  if (desc.includes('A6')) sizes.push('A6');
  if (desc.includes('DL')) sizes.push('DL');

  const paper: string[] = [];
  if (desc.includes('350gsm')) paper.push('350gsm');
  if (desc.includes('400gsm')) paper.push('400gsm');
  if (lower.includes('uncoated')) paper.push('Uncoated');

  const extras: string[] = [];
  if (lower.includes('matt')) extras.push('Matt lamination');
  if (lower.includes('gloss')) extras.push('Gloss lamination');
  if (lower.includes('silk')) extras.push('Silk finish');

  return { sizes, paper, extras };
}

const ASK = 'Ask our team for the options on this product';
const lines = (list: string[]) => (list.length ? list : [ASK]).map((text) => ({ text }));

export function getPrintDetailSections(product: PrintingProduct): PrintDetailSection[] {
  const o = PRODUCT_DETAILS[product.slug] ?? {};
  const parsed = parseFromDescription(product);

  const specification = [
    ...(o.minimumOrder ? [`Minimum order quantity: ${o.minimumOrder}`] : []),
    'Orientation options available',
    'Printed in full process colour',
    'Packaged in our high quality cardboard boxes',
    ...(o.specification ?? []),
  ];

  return [
    { title: 'Specification', items: lines(specification) },
    { title: 'Sizes', items: lines(o.sizes ?? parsed.sizes) },
    { title: 'Paper Stock', items: lines(o.paperStock ?? parsed.paper) },
    { title: 'Extras', items: lines(o.extras ?? parsed.extras) },
    { title: 'Artwork', items: ARTWORK_ITEMS },
    {
      title: 'Delivery & Turnaround',
      items: [
        { text: 'UK delivery available' },
        { text: 'FREE collection from our Kidderminster showroom' },
        { text: 'Our standard turnaround is 5–10 working days' },
        { text: 'Express production available, subject to availability' },
      ],
    },
  ];
}
